import {
  AuditIllustration,
  MediaBuyingIllustration,
  FunnelIllustration,
  TestingIllustration,
  ReportingIllustration,
} from "./ProcessIllustrations";
import { useScrollReveal } from "../../hooks/useScrollReveal";

const steps = [
  {
    num: "01",
    title: "Audit and diagnosis",
    body: "In the first week, we go deep on the existing account, funnel, and offer. We come out with a clear, prioritized list of what's broken and what we'd fix, and we walk you through the logic. No vague impressions. Specific findings, in order of impact.",
    Illustration: AuditIllustration,
  },
  {
    num: "02",
    title: "Media buying starts",
    body: "Both operators are in the account from day one. We set the account structure, build or inherit the campaign framework, and begin executing. Every decision (audiences, budgets, bid strategy, creative testing) goes through a senior operator.",
    Illustration: MediaBuyingIllustration,
  },
  {
    num: "03",
    title: "Funnel review and rebuild",
    body: "We rebuild or tighten the funnel: landing pages, offers, and conversion mechanics. We follow the real path your buyer takes, whether that ends in a checkout, a form, or a phone call, and fix wherever they drop off.",
    Illustration: FunnelIllustration,
  },
  {
    num: "04",
    title: "Testing and iteration",
    body: "We run structured tests on creative, audiences, and offers on a continuous basis. Decisions get made when the data says something, not on a planned calendar. When a test produces signal, we move on it. When it doesn't, we kill it fast and move budget elsewhere.",
    Illustration: TestingIllustration,
  },
  {
    num: "05",
    title: "Straight-talk reporting",
    body: "Every week: what we spent, what it produced, and what we are changing. Leads, cost per lead, booked calls, ROAS, whatever actually moves your business. Plain English, no vanity metrics.",
    Illustration: ReportingIllustration,
  },
];

function Step({ step, index }) {
  const isEven = index % 2 === 0;
  const { Illustration } = step;
  const [ref, vis] = useScrollReveal(0.1);

  return (
    <div
      ref={ref}
      data-testid={`process-step-${step.num}`}
      className="relative"
    >
      {/* Watermark number */}
      <div className={`watermark ${isEven ? 'left-0 md:-left-8' : 'right-0 md:-right-8'} top-0`}>
        {step.num}
      </div>

      <div className={`relative z-10 grid md:grid-cols-2 gap-12 md:gap-20 lg:gap-28 items-center ${
        isEven ? '' : 'md:[direction:rtl]'
      }`}>
        {/* Text */}
        <div className={`${isEven ? 'reveal-left' : 'reveal-right'} ${vis ? 'visible' : ''} ${!isEven ? 'md:[direction:ltr]' : ''}`}>
          <span className="step-num font-['Playfair_Display'] font-semibold text-[20px] md:text-[22px] block mb-3">
            Step {step.num}
          </span>
          <h3 className="font-['Playfair_Display'] font-semibold text-[28px] md:text-[34px] lg:text-[40px] text-[#0F172A] leading-[1.1] tracking-[-0.02em]">
            {step.title}
          </h3>
          <p className="mt-5 text-[16px] md:text-[17px] leading-[1.7] text-[#475569] max-w-[480px]">
            {step.body}
          </p>
        </div>

        {/* Illustration */}
        <div className={`illus-glow ${isEven ? 'reveal-right' : 'reveal-left'} ${vis ? 'visible' : ''} ${!isEven ? 'md:[direction:ltr]' : ''}`}>
          <Illustration />
        </div>
      </div>
    </div>
  );
}

export const OurProcess = () => {
  const [headerRef, headerVis] = useScrollReveal();

  return (
    <section
      id="process"
      data-testid="process-section"
      className="bg-white py-28 md:py-40 lg:py-48 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
        {/* Header */}
        <div ref={headerRef} className={`text-center max-w-[600px] mx-auto mb-24 md:mb-36 reveal ${headerVis ? 'visible' : ''}`}>
          <div className="editorial-rule mx-auto mb-6" />
          <h2
            data-testid="process-heading"
            className="font-['Playfair_Display'] font-semibold text-[clamp(28px,3.5vw,48px)] tracking-[-0.03em] text-[#0F172A] leading-[1.0]"
          >
            How it works from day one
          </h2>
        </div>

        {/* Steps */}
        <div className="space-y-28 md:space-y-40 lg:space-y-52">
          {steps.map((step, i) => (
            <Step key={step.num} step={step} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
